import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'ol/ol.css';
import { LineString, Polygon } from 'ol/geom';
import { getArea, getLength } from 'ol/sphere'
import Map from 'ol/Map';
import FullScreen from 'ol/control/FullScreen';
import proj4 from 'proj4';
import { get as getProjection, getTransform } from 'ol/proj';
import { register } from 'ol/proj/proj4';
import TileGrid from 'ol/tilegrid/TileGrid';
import View from 'ol/View';
import VectorLayer from 'ol/layer/Vector';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import VectorSource from 'ol/source/Vector';
import MVT from 'ol/format/MVT';
import styleFunction from 'ol-mapbox-style';
import TileLayer from 'ol/layer/WebGLTile';
import { DragBox, Select } from 'ol/interaction';
import Feature from 'ol/Feature';
import applyGeocoding from './geocoding.js';
import Hash from './hash';
import featuresStyle from './style.json';
import vectorlayers from './vectorlayers';
import { fromLonLat, get as projGet } from 'ol/proj'
import GeoTIFF from 'ol/source/GeoTIFF';



proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
proj4.defs("EPSG:3067", "+proj=utm +zone=35 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
register(proj4);

const params = (new URL(document.location)).searchParams,
  webLang = {
    fi: 'fi',
    fin: 'fi',
    'fi-FI': 'fi',
    sv: 'sv',
    swe: 'sv',
    'sv-FI': 'sv',
    en: 'en',
    eng: 'en'
  },

  normalizedLang = {
    fi: 'fin',
    fin: 'fin',
    'fi-FI': 'fin',
    sv: 'swe',
    swe: 'swe',
    'sv-FI': 'swe',
    en: 'eng',
    eng: 'eng'
  },
  lang = normalizedLang[params.get('lang')] || 'fin';


const texts = {
  fin: {
    state: {
      navigation_p1: 'Kartta on oletuksena navigointitilassa. Tällä painikkeella voit valita hiirellä maalaten kartalta aluerajauksen.',
      selection: 'Valitse alue kartalta hiirellä maalaten. Valinnan jälkeen oikealle listataan alueelta ortokuvien vuosiversiot ja linkit WCS palveluun',
      navigation_p2: 'Alla alueen ortokuvien vuosiversiot. Kartta on jälkeen navigointitilassa. Painikkeella voit tehdä uuden aluerajauksen.',
      navigation_p3: 'Rajaus on liian suuri. Kartta on jälkeen navigointitilassa. Painikkeella voit tehdä uuden aluerajauksen.'
    },
    el: {
      title: 'Ortokuvat ja korkeusmallit (WCS)',
      geocoding_reset: 'Tyhjennä haku',
      geocoding_title: 'Geokoodauspalvelu'

    },
    km2_text_plain: `WCS korkeusmalli 2m - text/plain`,
    km2_visualisointi: `WCS korkeusmalli 2m - visualisointi`,
    wcs_ortokuva_geotiff: `WCS ortokuva - image/tiff`,
    wcs_km2_ortokuva_visualisointi: `WCS korkeusmalli 2m ja ortokuva - visualisointi`,
    wcs_km2_varjostus_visualisointi: `WCS korkeusmalli 2m ja rinnevarjostus - visualisointi`,
    wcs_get_capabilities: `WCS GetCapabilities`,
    wcs_describe_coverage: `WCS DescribeCoverage`,
    wcs_features_landing_page: `OGC API Features kuvien metatiedot`,

    wcs_kartalle: 'Lisää GeoTIFF kartalle'

  },
  eng: {
    state: {
      navigation_p1: 'Map is in navigation and select mode. You may query ortophotos by clicking or drag and select with the blue button.',
      selection: 'Drag with mouse to select a bounding box. Ortophotos are fetched after mouse button is released.',
      navigation_p2: 'Ortophotos below. Map is back in navigation mode. Click on the map or use the blue button to drag and select',
      navigation_p3: 'Bounding box is too large. Map is back in navigation mode. Click on the map or use the blue button to drag and select'
    },
    el: {
      title: 'Ortophotos and elevation model (WCS)',
      geocoding_reset: 'Clear Search',
      geocoding_title: 'Geocoding'

    },
    km2_text_plain: `WCS elevation model 2m - text/plain`,
    km2_visualisointi: `WCS elevation model 2m - visualisation`,
    wcs_ortokuva_geotiff: `WCS ortophoto - image/tiff`,
    wcs_km2_ortokuva_visualisointi: `WCS elevation model 2m ja ortophoto - visualisation`,
    wcs_km2_varjostus_visualisointi: `WCS elevation model 2m with shades visualisation`,
    wcs_get_capabilities: `WCS GetCapabilities`,
    wcs_describe_coverage: `WCS Describe Coverage`,
    wcs_features_landing_page: `OGC API Features - metadata`,

    wcs_kartalle: 'Add GeoTIFF to Map'

  },
  swe: {
    state: {
      navigation_p1: 'Kartta on oletuksena navigointitilassa. Tällä painikkeella voit valita hiirellä maalaten kartalta aluerajauksen.',
      selection: 'Valitse alue kartalta hiirellä maalaten. Valinnan jälkeen oikealle listataan alueelta ortokuvien vuosiversiot ja linkit WCS palveluun',
      navigation_p2: 'Alla alueen ortokuvien vuosiversiot. Kartta on jälkeen navigointitilassa. Painikkeella voit tehdä uuden aluerajauksen.',
      navigation_p3: 'Bounding box is too large. Map is back in navigation mode. Click on the map or use the blue button to drag and select'

    },
    el: {
      title: 'Ortokuvat ja korkeusmallit (WCS)',
      geocoding_reset: 'Tyhjennä haku',
      geocoding_title: 'Geokoodauspalvelu'

    },
    km2_text_plain: `WCS korkeusmalli 2m - text/plain`,
    km2_visualisointi: `WCS korkeusmalli 2m - visualisointi`,
    wcs_ortokuva_geotiff: `WCS ortokuva - image/tiff`,
    wcs_km2_ortokuva_visualisointi: `WCS korkeusmalli 2m ja ortokuva - visualisointi`,
    wcs_km2_varjostus_visualisointi: `WCS elevation model 2m with shades visualisation`,
    wcs_get_capabilities: `WCS GetCapabilities`,
    wcs_describe_coverage: `WCS Describe Coverage`,
    wcs_features_landing_page: `Features Landing Page`,

    wcs_kartalle: 'Hae kartalle'

  }

},
  locale = texts[lang];

document.lang = webLang[lang];

const styleVer = 'v20',
  tileVer = 'v20',
  tileMatrixSet = 'ETRS-TM35FIN',
  epsg = 'EPSG:3067';

const apiKey = '7cd2ddae-9f2e-481c-99d0-404e7bc7a0b2',
  styleUrl = `https://avoin-karttakuva.maanmittauslaitos.fi/vectortiles/stylejson/${styleVer}/hobby.json?TileMatrixSet=${tileMatrixSet}&api-key=${apiKey}`,
  tileUrl = `https://avoin-karttakuva.maanmittauslaitos.fi/vectortiles/taustakartta/wmts/1.0.0/taustakartta/default/${tileVer}/${tileMatrixSet}/{z}/{y}/{x}.pbf?api-key=${apiKey}`
  ;

const
  bboxCrs = 'http%3A%2F%2Fwww.opengis.net%2Fdef%2Fcrs%2FEPSG%2F0%2F3067',
  crs = 'http%3A%2F%2Fwww.opengis.net%2Fdef%2Fcrs%2FEPSG%2F0%2F3067';

const wcs_coverages = ['ortokuva_vari', 'ortokuva_mustavalko', 'ortokuva_vaaravari', 'korkeusmalli_2m'];

const geotiffs = [];

const wcsApiVer = 'v2';

const wcsURLs = {
  'metadata': {
    landing_page: `https://avoin-karttakuva.maanmittauslaitos.fi/ortokuvat-ja-korkeusmallit/features/${wcsApiVer}`,
    collections: `https://avoin-karttakuva.maanmittauslaitos.fi/ortokuvat-ja-korkeusmallit/features/${wcsApiVer}/collections`
  },
  'features': {
    ortokuva_vari: {
      url: `https://avoin-karttakuva.maanmittauslaitos.fi/ortokuvat-ja-korkeusmallit/features/${wcsApiVer}/collections/ortokuva_vari/items?api-key=${apiKey}&f=json&bbox-crs=${bboxCrs}&crs=${crs}&bbox=`,
      varitieto: 'v'
    },
    ortokuva_mustavalko: {
      url: `https://avoin-karttakuva.maanmittauslaitos.fi/ortokuvat-ja-korkeusmallit/features/${wcsApiVer}/collections/ortokuva_mustavalko/items?api-key=${apiKey}&f=json&bbox-crs=${bboxCrs}&crs=${crs}&bbox=`,
      varitieto: 'mv'
    },
    ortokuva_vaaravari: {
      url: `https://avoin-karttakuva.maanmittauslaitos.fi/ortokuvat-ja-korkeusmallit/features/${wcsApiVer}/collections/ortokuva_vaaravari/items?api-key=${apiKey}&f=json&bbox-crs=${bboxCrs}&crs=${crs}&bbox=`,
      varitieto: 'vv'
    }
  },
  'wcs': `https://avoin-karttakuva.maanmittauslaitos.fi/ortokuvat-ja-korkeusmallit/wcs/${wcsApiVer}?api-key=${apiKey}&`,
  'wms': {
  },
  'wcs_wireframe': '../ortokuvat_ja_korkeusmallit_wcs_visualisointi/index.html?',
  'wcs_textured': '../ortokuvat_ja_korkeusmallit_wcs_visualisointi/index.html?texture=true&',
  'wcs_shaded': '../ortokuvat_ja_korkeusmallit_wcs_visualisointi/index.html??texture=true&',

}


let state = 'navigation_p1';


function setState(s, cls) {
  state = s;
  const el = document.getElementById('state');

  el.innerText = locale.state[state];
  el.classList.remove(...el.classList);
  if (cls) {
    debugger;
    cls.forEach(c => {
      el.classList.add(c);
    })
  }
}

const
  maxZoom = 14, extent =
    [-548576, 6291456, 1548576, 8388608],
  zoom = 12,
  center = [507474, 7433040],
  projection = getProjection(epsg),
  resolutions = [8192, 4096, 2048, 1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1, 0.5],
  tileGrid = new TileGrid({
    extent: extent,
    resolutions: resolutions,
    tileSize: [256, 256]
  });

projection.setExtent(extent);
const mapOpts = {
  dataProj: 'EPSG:3067',
  viewProj: 'EPSG:3067',
  dataCrs: 'http://www.opengis.net/def/crs/EPSG/0/3067',
  tileGridSet: 'ETRS-TM35FIN',
  featuresMinZoom: 11,
  styleFuncOptions: {
    resolutions: [8192, 4096, 2048, 1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1, 0.5, 0.25]
  },
  projection: projGet('EPSG:3067'),
  extent: [-548576, 6291456, 1548576, 8388608],
  adjustZ: 3
};

function clear(el) {
  while (el.firstChild) {
    el.firstChild.remove();
  }
}

function div(el) {
  const childEl = document.createElement('div');
  childEl.classList.add('entry');
  el.appendChild(childEl);
  return childEl;
}

function btn(el, text) {
  const childEl = document.createElement('button');
  childEl.classList.add('btn');
  childEl.classList.add('btn-primary');
  childEl.innerHTML = text;
  el.appendChild(childEl);
  return childEl;
}
function iconBtn(el, text, iconCls) {
  const childEl = document.createElement('button');
  childEl.classList.add('btn');
  childEl.classList.add('btn-light');
  const iconEl = document.createElement('i');
  iconEl.className = iconCls;
  childEl.appendChild(iconEl);
  el.appendChild(childEl);
  const textEl = document.createElement('span');
  textEl.style.paddingLeft = '1rem';
  textEl.innerHTML = text;
  childEl.appendChild(textEl);
  return childEl;
}

function link(el, url, title, target) {
  console.log("LINK", url, title);

  const childEl = document.createElement('a');
  if (url) {
    childEl.setAttribute('href', url);
  }
  if (target) {
    childEl.setAttribute('target', target);
  }
  childEl.innerHTML = title;

  el.appendChild(childEl);
  return childEl;
}

function image(el, url, title) {
  console.log("IMAGE", url, title);

  const childEl = document.createElement('img');
  childEl.setAttribute('width', '100%');
  childEl.setAttribute('src', url);

  el.appendChild(childEl);
}

function infoel(el, text) {
  const childEl = document.createElement('div');
  childEl.innerHTML = text;

  el.appendChild(childEl);

}

var wcsBoxesSource = new VectorSource({});


function wcsInfos(el) {
  const wcsInfoEl = div(el);

  link(wcsInfoEl, `${wcsURLs.wcs}service=WCS&request=GetCapabilities`, locale.wcs_get_capabilities, '_blank');
  link(wcsInfoEl, `${wcsURLs.metadata.landing_page}`, locale.wcs_features_landing_page, '_blank');

  wcs_coverages.forEach(key => {
    link(wcsInfoEl, `${wcsURLs.wcs}service=WCS&request=DescribeCoverage&CoverageID=${key}`, `${locale.wcs_describe_coverage} - ${key}`, '_blank');
  });

}

function wcs(bbox, geom, pixelBox, w, h) {

  const feat = new Feature({
    geometry: geom
  })


  wcsBoxesSource.clear();
  wcsBoxesSource.addFeature(feat);

  const el = document.getElementById('info');
  clear(el);

  wcsInfos(el);

  const commonInfoEl = div(el);

  infoel(commonInfoEl, `BBOX ${bbox.join(', ')}`);

  link(commonInfoEl, `${wcsURLs.wcs}${wcsKM2Parameters(bbox, 'korkeusmalli_2m')}`, locale.km2_text_plain, '_blank');
  link(commonInfoEl, `${wcsURLs.wcs_wireframe}${wcsVisualisationParameters(bbox, 'korkeusmalli_2m')}`, locale.km2_visualisointi, '_blank');
  //  link(commonInfoEl, `${wcsURLs.wcs_shaded}${wcsVisualisationParameters(bbox, 'korkeusmalli_2m')}`, locale.wcs_km2_varjostus_visualisointi, '_blank')

  const bboxValue = bbox.join(',');


  if (wcsURLs.wms['korkeusmalli_2m']) {
    image(commonInfoEl, `${wcsURLs.wms['korkeusmalli_2m'].url}${wmsParameters(bbox, wcsURLs.wms['korkeusmalli_2m'].layer, undefined, w, h)}`, `WMS`)
  }

  const v = [];



  for (const [key, value] of Object.entries(wcsURLs.features)) {

    const url = `${value.url}${bboxValue}`;

    v.push(fetch(url).then((r) => r.json()).then(json => {
      return { collection: key, varitieto: value.varitieto, features: json.features };
    }));
  }

  Promise.all(v).then(responses => {
    responses.forEach(r => console.log(r));

    responses.forEach(r => {

      const coll = r.collection,
        times = r.features.map(f => f.properties.time).sort().filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => {
          return a.time > b.time ? -1 : 1;
        }).reverse();

      console.log(coll, times);

      times.forEach(t => {
        const contEl = div(el);
        infoel(contEl, `${coll} - ${t}`);

        let geotiffurl = `${wcsURLs.wcs}${wcsParameters(bbox, coll, t)}`;
        let geotiffEl = iconBtn(contEl, locale.wcs_kartalle, 'bi bi-cart-plus');
        link(contEl, geotiffurl, 'GeoTIFF', '_blank');
        geotiffEl.dataset.geotiffurl = geotiffurl;
        geotiffEl.onclick = ((e) => {
          console.log(e, geotiffEl.dataset.geotiffurl);
          fetch_geotiff(map, geotiffEl.dataset.geotiffurl, e.target, contEl);

        });

        link(contEl, `${wcsURLs.wcs_textured}${wcsVisualisationParameters(bbox, coll, t)}`, locale.wcs_km2_ortokuva_visualisointi, '_blank')
        if (wcsURLs.wms[coll]) {
          image(contEl, `${wcsURLs.wms[coll].url}${wmsParameters(bbox, wcsURLs.wms[coll].layer, t, w, h)}`, `WMS`)
        }
      });

    })


  });

}

// service=WCS&version=2.0.1&request=GetCoverage&CoverageID=ortokuva&SUBSET=E(506931,507955)&SUBSET=N(7432531,7433155)&SUBSET=time(%222017-12-31T00:00:00Z%22)&format=image/tiff&
function wcsParameters(bbox, coverageID, time) {
  return `service=WCS&version=2.0.1&request=GetCoverage&CoverageID=${coverageID}&SUBSET=E(${bbox[0]},${bbox[2]})&SUBSET=N(${bbox[1]},${bbox[3]})&SUBSET=time(%22${time}%22)&format=image/tiff&`;
}

function wcsKM2Parameters(bbox, coverageID) {
  return `service=WCS&version=2.0.1&request=GetCoverage&CoverageID=${coverageID}&SUBSET=E(${bbox[0]},${bbox[2]})&SUBSET=N(${bbox[1]},${bbox[3]})&format=text/plain&`;
}

function wcsVisualisationParameters(bbox, coverageID, time, exaggeration) {
  const xag = exaggeration ? exaggeration : '1.0';
  if (time) {
    return `CoverageID=${coverageID}&SUBSET=E(${bbox[0]},${bbox[2]})&SUBSET=N(${bbox[1]},${bbox[3]})&TIME=${time}&EXAGGERATION=${xag}&`;
  } else {
    return `CoverageID=${coverageID}&SUBSET=E(${bbox[0]},${bbox[2]})&SUBSET=N(${bbox[1]},${bbox[3]})&EXAGGERATION=${xag}&`;
  }
}

// SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng&TRANSPARENT=true&LAYERS=ortokuva_mustavalko&TIME=1900%2F2000&TILED=true&WIDTH=256&HEIGHT=256&CRS=EPSG%3A3067&STYLES=&BBOX=506973.7005044967%2C7431745.800046047%2C507584.5121918515%2C7432356.611733402
function wmsParameters(bbox, layers, time, w, h) {
  let width = Math.floor(w), height = Math.floor(h);
  if (time) {
    return `SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng&TRANSPARENT=true&LAYERS=${layers}&TIME=${time}&CRS=EPSG%3A3067&STYLES=&BBOX=${bbox.join(',')}&WIDTH=${width}&HEIGHT=${height}`;
  } else {
    return `SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng&TRANSPARENT=true&LAYERS=${layers}&CRS=EPSG%3A3067&STYLES=&BBOX=${bbox.join(',')}&WIDTH=${width}&HEIGHT=${height}`;
  }
}

// Font replacement so we do not need to load web fonts in the worker
function getFont(font) {
  return 'sans-serif';
  /*return font[0]
    .replace('Noto Sans', 'sans-serif')
    .replace('Roboto', 'sans-serif');*/
}

const hash = new Hash(),
  map = new Map({
    target: 'map',
    view: new View({
      projection: projection,
      resolutions: resolutions,
      center: center,
      zoom: zoom,
      maxZoom: 16,
      minZoom: 1
    })
  });

hash.addTo(map);
map.addControl(new FullScreen());

// a DragBox interaction used to select features by drawing boxes
const dragBox = new DragBox({
  //  condition: platformModifierKeyOnly,
});
dragBox.setActive(false);
map.addInteraction(dragBox);

const bboxbutton = document.getElementById('bbox');
bboxbutton.onclick = () => {
  wcsBoxesSource.clear();
  const el = document.getElementById('info');
  clear(el);

  dragBox.setActive(true);
  setState('selection');
}

const cancelbutton = document.getElementById('cancel');
cancelbutton.onclick = () => {
  wcsBoxesSource.clear();
  const el = document.getElementById('info');
  clear(el);
  wcsInfos(el);

  geotiffs.forEach(l => {
    map.removeLayer(l);
  });
  geotiffs.clear();


  dragBox.setActive(false);
  setState('navigation_p1');
}

const searchbutton = document.getElementById('search');
searchbutton.onclick = () => {
  document.getElementById('cards_left').classList.toggle('hidden')
}

dragBox.on('boxend', function () {

  const sx = dragBox.box_.startPixel_[0],
    sy = dragBox.box_.startPixel_[1],
    ex = dragBox.box_.endPixel_[0],
    ey = dragBox.box_.endPixel_[1];
  const pixelBox =
    [Math.min(sx, ex), Math.min(sy, ey), Math.max(sx, ex), Math.max(sy, ey)];

  const w = pixelBox[2] - pixelBox[0],
    h = pixelBox[3] - pixelBox[1];


  const rotation = map.getView().getRotation();
  const oblique = rotation % (Math.PI / 2) !== 0;

  const geometry = dragBox.getGeometry().clone();

  if (!validateSelection(geometry)) {
    dragBox.setActive(false);
    setState('navigation_p3');
    return;
  }


  if (oblique) {
    const anchor = [0, 0];
    geometry.rotate(-rotation, anchor);
    const extent = geometry.getExtent();
    console.log("EXSTENT", extent, oblique);

    if (!validateSelection(geometry, extent)) {
      wcsBoxesSource.clear();
      const el = document.getElementById('info');
      clear(el);
      wcsInfos(el);

      setState('navigation_p3', ['alert', 'alert-primary']);
      return;
    }

    wcs(extent, geometry, pixelBox, w, h);
  } else {
    const extent = geometry.getExtent();
    console.log("EXSTENT", extent, oblique);

    if (!validateSelection(geometry, extent)) {
      wcsBoxesSource.clear();
      const el = document.getElementById('info');
      clear(el);
      wcsInfos(el);

      setState('navigation_p3', ['alert', 'alert-warning']);
      return;
    }

    wcs(extent, geometry, pixelBox, w, h);


  }
  dragBox.setActive(false);
  setState('navigation_p2');

});

function validateSelection(geom, extent) {

  if (geom instanceof Polygon) {
    if (!extent) {
      extent = geom.getExtent();
    }
    // 2000mx2000m

    let w = extent[2] - extent[0],
      h = extent[3] - extent[1];

    console.log("W", w, "H", h);

    if (w <= 2000 && h <= 2000) {
      return true
    }


  }

  return false;
}

// clear selection when drawing a new box and when clicking on the map
dragBox.on('boxstart', function () {
  //  selectedFeatures.clear();
});



(async (opts) => {

  const layers = await vectorlayers(opts.projection, tileGrid, opts.resolutions, featuresStyle,
    { 'kiinteisto-avoin': 9 }, { 'kiinteisto-avoin': 15 }, false);

  layers.forEach(l => {
    map.addLayer(l);
  });

  map.addLayer(new VectorLayer({
    source: wcsBoxesSource
  }));
  applyGeocoding(map);
  applyLocale();
  setState('navigation_p1');
})(mapOpts);

function applyLocale() {
  for (const [key, value] of Object.entries(locale.el)) {
    const localisedEl = document.getElementById(key);
    if (!localisedEl) {
      continue;
    }
    localisedEl.innerText = value;
  }


}

const el = document.getElementById('info');
clear(el);

wcsInfos(el);



function fetch_geotiff(map, geotiffurl, el, contEl) {

  console.log("FETCH URL", geotiffurl);

  fetch(geotiffurl)
    .then((response) => response.blob())
    .then((blob) => {


      const source = new GeoTIFF({
        sources: [
          {
            blob: blob
          },
        ],
      });

      source.on('change', () => {
        if (source.getState() === 'error') {
          console.error("ERRHANLDER", source.getError());
          div(contEl).innerHTML = source.getError();
        }
      });

      let geotifflayer = new TileLayer({
        source: source
      });

      map.addLayer(
        geotifflayer
      );

      geotiffs.push(geotifflayer);
    });

}


window.map = map;